import React, { useCallback, useContext, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { Picker } from "emoji-mart";

import apiV2 from "../../services/apiV2";
import toastError from "../../errors/toastError";
import { resolveMediaUrl } from "../../helpers/resolveMediaUrl";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { toast } from "react-toastify";
import "./StickerPicker.css";

const PICKER_WIDTH = 360;
const PICKER_HEIGHT = 440;

function isAdminUser(user) {
	const profile = String(user?.profile || "").toLowerCase();
	return profile === "admin" || profile === "master";
}

export default function TailwickComposerEmojiPicker({
	open,
	anchorEl,
	onClose,
	onSelectEmoji,
	onSelectSticker,
	onUploadNew,
	darkMode,
	disabled,
	initialTab = "emoji",
}) {
	const { user } = useContext(AuthContext);
	const isAdmin = isAdminUser(user);
	const [menuStyle, setMenuStyle] = useState(null);
	const [mainTab, setMainTab] = useState(initialTab || "emoji");
	const [packTab, setPackTab] = useState("user");
	const [userStickers, setUserStickers] = useState([]);
	const [companyStickers, setCompanyStickers] = useState([]);
	const [loading, setLoading] = useState(false);
	const [deletingId, setDeletingId] = useState(null);

	useEffect(() => {
		if (open) {
			setMainTab(initialTab || "emoji");
		}
	}, [open, initialTab]);

	const loadStickers = useCallback(async () => {
		setLoading(true);
		try {
			const { data } = await apiV2.get("/stickers");
			setUserStickers(data?.userStickers || data?.stickers || []);
			setCompanyStickers(data?.companyStickers || []);
		} catch (err) {
			toastError(err);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		if (!open) return undefined;
		if (mainTab === "stickers") {
			loadStickers();
		}
		return undefined;
	}, [open, mainTab, loadStickers]);

	useEffect(() => {
		if (!open || !anchorEl) {
			setMenuStyle(null);
			return undefined;
		}

		const updatePosition = () => {
			const rect = anchorEl.getBoundingClientRect();
			const width = Math.min(PICKER_WIDTH, window.innerWidth - 16);
			let left = rect.left + rect.width / 2 - width / 2;
			left = Math.max(8, Math.min(left, window.innerWidth - width - 8));

			const spaceAbove = rect.top;
			const spaceBelow = window.innerHeight - rect.bottom;
			const preferAbove = spaceAbove >= spaceBelow;

			if (preferAbove) {
				setMenuStyle({
					top: rect.top - 8,
					left,
					width,
					transform: "translateY(-100%)",
				});
				return;
			}

			setMenuStyle({
				top: rect.bottom + 8,
				left,
				width,
				transform: "none",
			});
		};

		updatePosition();
		window.addEventListener("resize", updatePosition);
		window.addEventListener("scroll", updatePosition, true);

		return () => {
			window.removeEventListener("resize", updatePosition);
			window.removeEventListener("scroll", updatePosition, true);
		};
	}, [open, anchorEl]);

	useEffect(() => {
		if (!open) return undefined;

		const handlePointerDown = (event) => {
			const node = document.getElementById("composer-emoji-sticker-picker");
			if (node?.contains(event.target)) return;
			if (anchorEl?.contains(event.target)) return;
			onClose();
		};

		document.addEventListener("mousedown", handlePointerDown);
		return () => document.removeEventListener("mousedown", handlePointerDown);
	}, [open, anchorEl, onClose]);

	const handleDelete = async (event, sticker) => {
		event.preventDefault();
		event.stopPropagation();
		if (!sticker?.id || deletingId) return;

		const scope = packTab === "company" ? "company" : "user";
		if (scope === "company" && !isAdmin) {
			toast.error(i18n.t("messagesInput.stickerPicker.deleteCompanyForbidden"));
			return;
		}

		setDeletingId(sticker.id);
		try {
			await apiV2.delete(`/stickers/${sticker.id}`, {
				params: { scope },
			});
			if (scope === "company") {
				setCompanyStickers((prev) => prev.filter((s) => s.id !== sticker.id));
				toast.success(i18n.t("messagesInput.stickerPicker.deletedCompany"));
			} else {
				setUserStickers((prev) => prev.filter((s) => s.id !== sticker.id));
				setCompanyStickers((prev) => prev.filter((s) => s.id !== sticker.id));
				toast.success(i18n.t("messagesInput.stickerPicker.deleted"));
			}
		} catch (err) {
			toastError(err);
		} finally {
			setDeletingId(null);
		}
	};

	if (!open || !menuStyle) return null;

	const list = packTab === "company" ? companyStickers : userStickers;
	const canDeleteOnTab = packTab === "user" || isAdmin;

	const picker = (
		<div
			id="composer-emoji-sticker-picker"
			className="sticker-picker sticker-picker--wa"
			data-theme={darkMode ? "dark" : "light"}
			style={{
				position: "fixed",
				top: menuStyle.top,
				left: menuStyle.left,
				width: menuStyle.width,
				height: PICKER_HEIGHT,
				transform: menuStyle.transform,
				zIndex: 1400,
			}}
		>
			<div className="sticker-picker__body">
				{mainTab === "stickers" ? (
					<>
						<div className="sticker-picker__header">
							<span className="sticker-picker__title">
								{i18n.t("messagesInput.stickerPicker.title")}
							</span>
							<label
								className={`sticker-picker__upload${
									disabled ? " sticker-picker__upload--disabled" : ""
								}`}
							>
								<input
									type="file"
									accept="image/webp,.webp"
									disabled={disabled}
									className="hidden"
									onChange={(event) => {
										if (onUploadNew) onUploadNew(event);
										onClose();
									}}
								/>
								{i18n.t("messagesInput.stickerPicker.upload")}
							</label>
						</div>

						<div className="sticker-picker__tabs" role="tablist">
							<button
								type="button"
								role="tab"
								aria-selected={packTab === "user"}
								className={`sticker-picker__tab${
									packTab === "user" ? " sticker-picker__tab--active" : ""
								}`}
								onClick={() => setPackTab("user")}
							>
								{i18n.t("messagesInput.stickerPicker.mine")}
							</button>
							<button
								type="button"
								role="tab"
								aria-selected={packTab === "company"}
								className={`sticker-picker__tab${
									packTab === "company" ? " sticker-picker__tab--active" : ""
								}`}
								onClick={() => setPackTab("company")}
							>
								{i18n.t("messagesInput.stickerPicker.company")}
							</button>
						</div>

						<div className="sticker-picker__grid">
							{loading ? (
								<p className="sticker-picker__empty">
									{i18n.t("messagesInput.stickerPicker.loading")}
								</p>
							) : list.length === 0 ? (
								<p className="sticker-picker__empty">
									{packTab === "company"
										? i18n.t("messagesInput.stickerPicker.emptyCompany")
										: i18n.t("messagesInput.stickerPicker.emptyMine")}
								</p>
							) : (
								list.map((sticker) => {
									const src = resolveMediaUrl(sticker.mediaUrl);
									return (
										<div key={sticker.id} className="sticker-picker__item-wrap">
											<button
												type="button"
												className="sticker-picker__item"
												disabled={disabled}
												onClick={() => {
													if (onSelectSticker) onSelectSticker(sticker);
													onClose();
												}}
												title={i18n.t("messagesInput.stickerPicker.send")}
											>
												<img src={src} alt="" loading="lazy" />
											</button>
											{canDeleteOnTab && (
												<button
													type="button"
													className="sticker-picker__delete"
													disabled={disabled || deletingId === sticker.id}
													onClick={(event) => handleDelete(event, sticker)}
													title={i18n.t("messagesInput.stickerPicker.remove")}
													aria-label={i18n.t("messagesInput.stickerPicker.remove")}
												>
													×
												</button>
											)}
										</div>
									);
								})
							)}
						</div>
					</>
				) : (
					<div className="sticker-picker__emoji-wrap">
						<Picker
							perLine={9}
							showPreview={false}
							showSkinTones={false}
							theme={darkMode ? "dark" : "light"}
							onSelect={onSelectEmoji}
							style={{ width: "100%" }}
						/>
					</div>
				)}
			</div>

			<div className="sticker-picker__main-tabs" role="tablist">
				<button
					type="button"
					role="tab"
					aria-selected={mainTab === "emoji"}
					className={`sticker-picker__main-tab${
						mainTab === "emoji" ? " sticker-picker__main-tab--active" : ""
					}`}
					onClick={() => setMainTab("emoji")}
					title={i18n.t("messagesInput.stickerPicker.emojiTab")}
				>
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
						<circle cx="12" cy="12" r="10" />
						<path d="M8 14s1.5 2 4 2 4-2 4-2" />
						<line x1="9" y1="9" x2="9.01" y2="9" />
						<line x1="15" y1="9" x2="15.01" y2="9" />
					</svg>
					<span>{i18n.t("messagesInput.stickerPicker.emojiTab")}</span>
				</button>
				<button
					type="button"
					role="tab"
					aria-selected={mainTab === "stickers"}
					className={`sticker-picker__main-tab${
						mainTab === "stickers" ? " sticker-picker__main-tab--active" : ""
					}`}
					onClick={() => setMainTab("stickers")}
					title={i18n.t("messagesInput.stickerPicker.title")}
				>
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
						<path d="M12 3 4 7v6c0 5 3.5 9 8 10 4.5-1 8-5 8-10V7l-8-4Z" />
					</svg>
					<span>{i18n.t("messagesInput.stickerPicker.title")}</span>
				</button>
			</div>
		</div>
	);

	return ReactDOM.createPortal(picker, document.body);
}
